import React from 'react';
import { motion } from 'framer-motion';
import {
  Award,
  GraduationCap,
  Briefcase,
  Heart,
  Stethoscope,
  Star,
  Calendar,
  MapPin,
  ChevronLeft,
  Baby,
  Facebook,
  Video
} from 'lucide-react';
import './About.css';

const education = [
  {
    year: 'الماجستير',
    title: 'ماجستير النساء والتوليد',
    institution: 'جامعة الأزهر',
    desc: 'تخصص دقيق في النساء والتوليد مع التركيز على علاج تأخر الحمل وتقنيات الإخصاب المساعد.',
  },
  {
    year: 'البكالوريوس',
    title: 'بكالوريوس الطب والجراحة',
    institution: 'جامعة الأزهر',
    desc: 'التخرج بتقدير امتياز مع مرتبة الشرف.',
  },
];

const experience = [
  {
    year: 'الآن',
    title: 'استشاري النساء والتوليد',
    place: 'عيادات متعددة - القاهرة',
    desc: 'استشاري النساء والتوليد بخبرة 20 سنة، متخصص في علاج تأخر الحمل والحقن المجهري والتشخيص الوراثي للأجنة.',
  },
  {
    year: '20+ سنة',
    title: 'خبرة في مجال النساء والتوليد',
    place: 'مستشفيات ومراكز متخصصة',
    desc: 'متابعة الحمل والحمل الحرج، علاج تأخر الحمل، تقنيات الإخصاب المساعد (ICSI)، التشخيص الوراثي للأجنة قبل الانغماس (PGD)، المناظير الجراحية.',
  },
];

const certifications = [
  'ماجستير النساء والتوليد - جامعة الأزهر',
  'متخصص في علاج تأخر الحمل',
  'شهادة في تقنيات الإخصاب المساعد (ICSI)',
  'شهادة في التشخيص الوراثي للأجنة (PGD)',
  'متخصص في المناظير الجراحية',
  'متابعة الحمل والحمل الحرج',
];

const clinics = [
  {
    name: 'المقطم',
    address: 'عمارة 163 بجوار صيدلية ناريمان',
    times: 'الأحد والثلاثاء: 8 - 11 م | الخميس: 1 - 4 ظ',
    phone: '01028478060 - 0226688042',
    map: 'https://maps.app.goo.gl/Vcxx1Gh9nMw8PWL68',
  },
  {
    name: 'مدينة نصر - مركز كابيتال للإنجاب',
    address: '49 شارع محيي الدين عبد الحميد من عباس العقاد',
    times: 'الاثنين والخميس: 10 - 12 ص',
    phone: '01140147994',
    map: '',
  },
  {
    name: 'الشروق - بانوراما مول',
    address: 'الشروق - بانوراما مول',
    times: 'مواعيد متعددة طوال الأسبوع',
    phone: '01104044537',
    map: '',
  },
  {
    name: 'مستشفى الزراعيين - الدقي',
    address: 'مستشفى الزراعيين',
    times: 'السبت: 12 - 2 م | الأربعاء: 10 - 12 م',
    phone: '02333776677 داخلي 161',
    map: '',
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.6 },
};

const About = () => {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="about-hero-bg">
          <div className="about-hero-gradient"></div>
        </div>
        <div className="about-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="about-hero-badge"
          >
            <Star size={14} />
            <span>تعرفي على طبيبكِ</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            د. محمد خالد توفيق
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="about-hero-subtitle"
          >
            استشاري النساء والتوليد - ماجستير النساء والتوليد جامعة الأزهر - 20+ سنة خبرة
          </motion.p>
        </div>
      </section>

      {/* Profile Section */}
      <section className="profile-section">
        <div className="section-container">
          <div className="profile-grid">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="profile-image"
            >
              <div className="profile-image-wrapper">
                <div className="profile-image-glow"></div>
                <img
                  src='/Assets/WhatsApp Image 2026-07-12 at 1.14.36 PM.jpeg'
                  alt="د. محمد خالد توفيق"
                  className="profile-img"
                />
                <div className="profile-badge">
                  <Award size={20} />
                  <span>20+ سنة خبرة</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="profile-info"
            >
              <h2>قصة نجاح تمتد لأكثر من 20 عاماً</h2>
              <p className="profile-bio">
                د. محمد خالد توفيق هو استشاري النساء والتوليد بخبرة تمتد لأكثر من 20 عاماً.
                حصل على ماجستير النساء والتوليد من جامعة الأزهر، وتميز بتقديم أعلى مستويات الرعاية الطبية
                في مجال النساء والتوليد.
              </p>
              <p className="profile-bio">
                يتخصص د. محمد في علاج تأخر الحمل وتقنيات الإخصاب المساعد (ICSI) والتشخيص الوراثي للأجنة قبل الانغماس (PGD)،
                بالإضافة إلى المناظير الجراحية ومتابعة الحمل والحمل الحرج.
                يؤمن بأن كل مريضة تستحق رعاية شخصية فريدة.
              </p>

              <div className="profile-stats">
                {/* <div className="profile-stat">
                  <Baby size={24} />
                  <div>
                    <span className="stat-number">10,000+</span>
                    <span className="stat-text">مريضة سعيدة</span>
                  </div>
                </div> */}
                <div className="profile-stat">
                  <Stethoscope size={24} />
                  <div>
                    <span className="stat-number">20+</span>
                    <span className="stat-text">سنة خبرة</span>
                  </div>
                </div>
                <div className="profile-stat">
                  <Award size={24} />
                  <div>
                    <span className="stat-number">98%</span>
                    <span className="stat-text">نسبة النجاح</span>
                  </div>
                </div>
              </div>

              <div className="profile-contact">
                <div className="profile-contact-item">
                  <MapPin size={16} />
                  <span>المقطم - مدينة نصر - الدقي</span>
                </div>
                <div className="profile-contact-item">
                  <Calendar size={16} />
                  <span>مواعيد متعددة طوال الأسبوع</span>
                </div>
              </div>

              {/* <div className="profile-social">
                <a href="https://www.facebook.com/share/1DGkN6yJi7/" target="_blank" rel="noopener noreferrer" className="social-link facebook">
                  <Facebook size={20} />
                  <span>فيسبوك</span>
                </a>
                <a href="https://www.tiktok.com/@dr.mohamed.khaled.t?_r=1&_t=ZS-97wtDkci8aw" target="_blank" rel="noopener noreferrer" className="social-link tiktok">
                  <Video size={20} />
                  <span>تيك توك</span>
                </a>
              </div> */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Clinics Section */}
      {/* <section className="clinics-section">
        <div className="section-container">
          <motion.div {...fadeInUp} className="section-header">
            <span className="section-badge">عياداتنا</span>
            <h2 className="section-title">مواقع العيادات والمواعيد</h2>
          </motion.div>

          <div className="clinics-grid">
            {clinics.map((clinic, index) => (
              <motion.div
                key={clinic.name}
                {...fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="clinic-card"
                whileHover={{ y: -5 }}
              >
                <div className="clinic-header">
                  <MapPin size={24} className="clinic-icon" />
                  <h3>{clinic.name}</h3>
                </div>
                <div className="clinic-details">
                  <div className="clinic-detail">
                    <MapPin size={16} />
                    <span>{clinic.address}</span>
                  </div>
                  <div className="clinic-detail">
                    <Calendar size={16} />
                    <span>{clinic.times}</span>
                  </div>
                  <div className="clinic-detail">
                    <Stethoscope size={16} />
                    <span>{clinic.phone}</span>
                  </div>
                </div>
                {clinic.map && (
                  <a href={clinic.map} target="_blank" rel="noopener noreferrer" className="clinic-map-link">
                    عرض على الخريطة
                    <ChevronLeft size={14} />
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Education Timeline */}
      <section className="timeline-section">
        <div className="section-container">
          <motion.div {...fadeInUp} className="section-header">
            <span className="section-badge">المؤهلات العلمية</span>
            <h2 className="section-title">رحلة التعلم والتخصص</h2>
          </motion.div>

          <div className="timeline">
            {education.map((item, index) => (
              <motion.div
                key={item.year}
                {...fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="timeline-item"
              >
                <div className="timeline-marker">
                  <GraduationCap size={18} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-year">{item.year}</span>
                  <h3>{item.title}</h3>
                  <h4>{item.institution}</h4>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Timeline */}
      <section className="timeline-section experience-section">
        <div className="section-container">
          <motion.div {...fadeInUp} className="section-header">
            <span className="section-badge">الخبرة المهنية</span>
            <h2 className="section-title">مسيرة مهنية حافلة</h2>
          </motion.div>

          <div className="timeline">
            {experience.map((item, index) => (
              <motion.div
                key={item.year}
                {...fadeInUp}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="timeline-item"
              >
                <div className="timeline-marker experience">
                  <Briefcase size={18} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-year">{item.year}</span>
                  <h3>{item.title}</h3>
                  <h4>{item.place}</h4>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="certs-section">
        <div className="section-container">
          <motion.div {...fadeInUp} className="section-header">
            <span className="section-badge">الشهادات والتخصصات</span>
            <h2 className="section-title">اعتراف بتميزه</h2>
          </motion.div>

          <div className="certs-grid">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert}
                {...fadeInUp}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="cert-card"
                whileHover={{ scale: 1.03 }}
              >
                <Award size={24} className="cert-icon" />
                <span>{cert}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;